<template>
  <div class="flex items-center">
    <NBox
      v-gid="{
        module: 'order',
        screen: 'home',
        section: 'conditionalForm',
        component: 'conditionalOrderDescription',
      }"
      class="flex items-center flex-1 p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mb-2"
      @click="openConditionalOrderDescriptionDialog"
    >
      <img
        src="@/assets/images/conditional-order.svg"
        alt="conditional_order"
        width="24"
        height="24"
      />

      <NText
        variant="caption1"
        weight="bold"
        class="mr-2 text-neutral-secondary"
      >
        {{ t('orders.conditional.whatAreConditionalTrading') }}
      </NText>
    </NBox>

    <NBox
      class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mr-2 mb-2 flex-1"
      @click="openAlgorithmOrderRiskDescriptionDialog('conditional')"
    >
      <div
        class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
      >
        <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
      </div>

      <NText
        variant="caption1"
        weight="bold"
        class="mr-2 text-neutral-secondary"
      >
        {{ t('orders.riskAnnouncement.title') }}
      </NText>
    </NBox>
  </div>

  <VeeForm v-slot="{ errors, handleSubmit }" as="">
    <form
      autocomplete="off"
      class="p-4 rounded-md w-full overflow-auto"
      :class="
        isOrderBuy
          ? 'bg-brand-primary bg-opacity-10 border border-brand-primary border-opacity-10'
          : 'bg-critical-secondary bg-opacity-90 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 darkTheme:bg-opacity-50'
      "
      @submit="handleSubmit($event, onSubmit)"
    >
      <!-- Bonus Share -->
      <template v-if="!isOrderBuy && Boolean(bonusShare)">
        <div
          class="flex justify-start items-center gap-1 mb-2 flex-1 cursor-pointer"
          @click="
            openBonusShareDialog(getSymbolName(symbol), bonusShare?.quantity)
          "
        >
          <NegarErrorBoldIcon size="16" class="text-warning-secondary" />

          <NText
            variant="overline"
            class="text-neutral-secondary whitespace-nowrap text-ellipsis overflow-hidden"
          >
            {{ t('سهام جایزه شما تا زمان تبدیل شدن') }}
            <NText variant="overline" weight="bold">
              {{ t('غیر قابل معامله') }}
            </NText>
            {{ t('است.') }}
          </NText>

          <NegarArrowLeftIcon size="16" />
        </div>
      </template>

      <NRadioGroup
        v-model="orderCondition"
        :required-sign="false"
        required
        class="!mb-0"
      >
        <NRadio
          class="relative w-full"
          :value="ORDER_CONDITIONS.quantity"
          @click="formModel.totalValue = null"
        >
          <VeeField
            v-slot="{ field }"
            v-model="formModel.quantity"
            name="quantity"
            :rules="quantityValidationRules"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              v-focus
              :model-value="formModel.quantity"
              input-class="text-center font-bold text-neutral-primary"
              v-bind="field"
              :error="errors.quantity || !isValidTotalValue"
              :required-sign="false"
              type="tel"
              dir="ltr"
              class="!mb-0"
              :disabled="orderCondition === ORDER_CONDITIONS.value"
              required
              :step="symbol.lotSize"
              :min="0"
              size="small"
              @focusin="$event.target.select()"
              @update:model-value="changeQuantity($event)"
            >
              <template #prefix>
                <NText
                  variant="caption1"
                  weight="medium"
                  class="text-neutral-primary"
                >
                  {{ t('misc.quantity') }}
                </NText>
              </template>
            </NInput>
          </VeeField>
        </NRadio>

        <ErrorMessage v-slot="{ message }" name="quantity">
          <NText
            weight="regular"
            variant="caption2"
            class="text-critical-primary"
          >
            {{ message }}
          </NText>
        </ErrorMessage>

        <template
          v-if="
            !isOrderBuy &&
            portfolioQuantity &&
            orderCondition === ORDER_CONDITIONS.quantity
          "
        >
          <div class="flex justify-between items-center px-3 mt-1">
            <NText variant="caption1">
              {{ `${t('modules.order.portfolio_quantity')}:` }}
            </NText>

            <div>
              <NumberFormatter #="{ number }" :value="portfolioQuantity">
                <NText
                  variant="caption1"
                  class="cursor-pointer"
                  @click="changeQuantity(portfolioQuantity)"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </div>
          </div>

          <Slider
            :model-value="formModel.quantity ?? 0"
            :max-value="portfolioQuantity"
            color="red"
            @change="changeQuantity"
          />
        </template>

        <NRadio
          class="relative w-full mt-2"
          :value="ORDER_CONDITIONS.value"
          @click="formModel.quantity = null"
        >
          <VeeField
            v-slot="{ field }"
            v-model="formModel.totalValue"
            name="totalValue"
            :rules="totalValueValidationRules"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              :model-value="formModel.totalValue"
              v-bind="field"
              input-class="text-center font-bold text-neutral-primary"
              :error="errors.totalValue"
              :required-sign="false"
              :disabled="orderCondition === ORDER_CONDITIONS.quantity"
              type="tel"
              dir="ltr"
              class="!mb-0"
              size="small"
              :min="0"
              required
              @focusin="$event.target.select()"
              @update:model-value="changeTotalValue($event)"
            >
              <template #prefix>
                <NText
                  variant="caption1"
                  weight="medium"
                  class="text-neutral-primary"
                >
                  {{ t('misc.value') }}
                </NText>
              </template>

              <template #postfix>
                <NText variant="caption1" class="text-neutral-secondary">
                  {{ t('misc.rial') }}
                </NText>
              </template>
            </NInput>
          </VeeField>
        </NRadio>

        <ErrorMessage v-slot="{ message }" name="totalValue">
          <NText
            weight="regular"
            variant="caption2"
            class="text-critical-primary"
          >
            {{ message }}
          </NText>
        </ErrorMessage>
      </NRadioGroup>

      <template v-if="isOrderBuy && orderCondition === ORDER_CONDITIONS.value">
        <div
          class="flex justify-between items-center px-3 mt-2"
          :class="{
            'text-neutral-tertiary-disable': cash.buyingPower <= 0,
          }"
        >
          <NText variant="caption1">
            {{ t('common.buy_power') }}
          </NText>

          <NLoading v-if="isLoadingCash" class="text-primary" />

          <div v-else>
            <NumberFormatter #="{ number }" :value="cash.buyingPower">
              <NText
                dir="ltr"
                variant="caption1"
                class="cursor-pointer"
                :class="{ 'cursor-default': cash.buyingPower <= 0 }"
                @click="changeTotalValue(cash.buyingPower)"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
            <NText
              variant="caption2"
              :class="{
                'text-neutral-tertiary-disable': cash.buyingPower <= 0,
              }"
              class="text-neutral-secondary ms-0.5 align-middle"
            >
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <Slider
          v-if="cash.buyingPower && cash.buyingPower > 0"
          :model-value="formModel.totalValue ?? 0"
          :max-value="cash.buyingPower"
          @change="changeTotalValue"
        />
      </template>

      <div class="inline-flex gap-1 mb-1 mt-3">
        <NText variant="caption1" weight="bold">
          {{ t('orders.conditional.conditionOfPrice') }}
        </NText>

        <NegarInfoWithBorderIcon
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'conditionalForm',
            component: 'conditionalPriceDescription',
          }"
          size="20"
          class="cursor-pointer"
          @click="openConditionalOrderPriceDescriptionDialog"
        />
      </div>

      <VeeField
        v-slot="{ field }"
        v-model="formModel.price"
        name="price"
        :rules="{ required: [t('common.price')] }"
      >
        <div class="flex gap-1">
          <NListbox v-model="formModel.priceCondition" input-size="small">
            <NListboxOption v-bind="PRICE_CONDITIONS.upperThreshold">
              {{ t('common.upperThan') }}
            </NListboxOption>

            <NListboxOption v-bind="PRICE_CONDITIONS.lowerThreshold">
              {{ t('common.lowerThan') }}
            </NListboxOption>
          </NListbox>

          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            :model-value="formModel.price"
            input-class="text-center font-bold"
            v-bind="field"
            :error="errors.price"
            :required-sign="false"
            type="tel"
            dir="ltr"
            required
            class="!w-[36rem] !mb-0"
            :min="0"
            :step="symbol.fixedPriceTick"
            size="small"
            @focusin="$event.target.select()"
            @update:model-value="changePrice($event)"
          >
            <template #postfix>
              <NText variant="caption1" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </template>
          </NInput>
        </div>

        <NText
          v-if="realValue && orderCondition === ORDER_CONDITIONS.quantity"
          variant="overline"
          weight="medium"
          class="block pr-3 text-neutral-secondary-press mt-1"
        >
          {{
            isOrderBuy
              ? t('orders.conditional.receiveAmountMessage.buy', {
                  amount: realValueWords,
                })
              : t('orders.conditional.receiveAmountMessage.sell', {
                  amount: realValueWords,
                })
          }}
        </NText>

        <NText
          v-if="formModel.price && orderCondition === ORDER_CONDITIONS.value"
          variant="overline"
          weight="medium"
          class="block pr-3 text-neutral-secondary-press mt-1"
        >
          {{
            isOrderBuy
              ? t('orders.conditional.numberOfSoldStockMessage.buy', {
                  quantity,
                })
              : t('orders.conditional.numberOfSoldStockMessage.sell', {
                  quantity,
                })
          }}
        </NText>

        <ErrorMessage v-slot="{ message }" name="price">
          <NText
            weight="medium"
            variant="overline"
            class="block pr-3 text-critical-primary mt-1"
          >
            {{ message }}
          </NText>
        </ErrorMessage>
      </VeeField>

      <!-- <NText
        v-if="!isValidTotalValue"
        weight="medium"
        variant="overline"
        class="block pr-3 text-critical-primary mt-1"
      >

        {{
          t('orders.conditional.minimumTotalValueValidationMessage', {
            value: numberFormatter(minOrderValue),
          })
        }}
      </NText> -->

      <ConditionalValidity v-model="formModel.validityDate" class="mb-32" />

      <div
        class="bg-neutral-primary border-t border-t-neutral-primary w-full p-3 rounded-b-md absolute bottom-0 right-0 z-20"
        :class="
          isOrderBuy
            ? 'border border-brand-primary border-opacity-10'
            : 'border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10'
        "
      >
        <NBox
          v-if="PETROCHEMICALS_ISINS.includes(symbol.isin!)"
          class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mb-6"
          @click="openPetrochemicalsOrderRiskDescriptionDialog()"
        >
          <div
            class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
          >
            <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
          </div>

          <NText
            variant="caption1"
            weight="bold"
            class="mr-2 text-neutral-secondary"
          >
            {{ t('orders.symbolUnderCautionTitle') }}
          </NText>
        </NBox>

        <div class="flex gap-2 items-center justify-center">
          <NButton
            v-if="isOrderBuy"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'buyConditionalOrderDialog',
              component: 'submitOrderButton',
              disabled: isLoadingCreateConditionalOrder,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            size="small"
            :loading="isLoadingCreateConditionalOrder"
            :disabled="isLoadingCreateConditionalOrder"
          >
            {{ t('modules.order.sendBuyOrder') }}
          </NButton>

          <NButton
            v-else
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'sellConditionalOrderDialog',
              component: 'submitOrderButton',
              disabled: isLoadingCreateConditionalOrder,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            color="red"
            size="small"
            :loading="isLoadingCreateConditionalOrder"
            :disabled="isLoadingCreateConditionalOrder"
          >
            {{ t('modules.order.sendSellOrder') }}
          </NButton>

          <NButton
            v-gid="{
              module: 'order',
              screen: 'home',
              section: `${type}ConditionalOrderDialog`,
              component: 'sendAndCloseButton',
              disabled: isLoadingCreateConditionalOrder,
            }"
            variant="outline"
            class="font-bold text-c1"
            :color="isOrderBuy ? 'green' : 'red'"
            size="small"
            :loading="isLoadingCreateConditionalOrder"
            :disabled="isLoadingCreateConditionalOrder"
            @click="
              () => {
                shouldCloseDialogAfterOrderExecution = true;
                handleSubmit();
              }
            "
          >
            {{ t('modules.order.sendAndClose') }}
          </NButton>
        </div>
      </div>
    </form>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLiveCashQuery } from '@core/cash';
import { ErrorMessage } from 'vee-validate';
import {
  useSymbolDeductions,
  calcSymbolBuyValue,
  calcSymbolQuantityBuy,
  calcSymbolQuantitySell,
  calcSymbolSellValue,
  PETROCHEMICALS_ISINS,
  getSymbolName,
} from '@core/symbol';
import { Instrument, PortfolioItem } from '@libs/api-reference';
import {
  CONDITIONAL_ORDER_VALIDITY_TIME_PERIOD,
  MINIMUM_CONDITIONAL_TOTAL_VALUE,
} from '@core/order';
import { NBox, NText, useDialog } from '@negar/core';
import {
  NegarInfoWithBorderIcon,
  NegarErrorBoldIcon,
  NegarArrowLeftIcon,
} from '@negar/icons';
import { num2mixed } from 'num2persian';
import { omitBy } from '@libs/utils';

import { useCreateOrder, useOrderDialogs } from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';
import Slider from '@/components/Slider.vue';

import vMask from '@/directives/mask';
import vFocus from '@/directives/focus';

import eventBus from '@/composables/event/eventBus';

import ConditionalValidity from './ConditionalValidity.vue';
import { usePortfolioDialogs } from '@/modules/portfolio';

type FormModel = {
  quantity?: number | null;
  price?: number | null;
  upperThreshold?: number | null;
  lowerThreshold?: number | null;
  totalValue?: number | null;
  executedQuantity: number | null;
  validityDate: number;
  priceCondition: { value: string; label: string };
  bypassSellQuantity: boolean;
};

type OrderFormProps = {
  type: 'sell' | 'buy';
  symbol: Instrument;
  portfolioQuantity: number;
  model: {
    quantity?: null | number;
    price?: null | number;
  };
  dialogId?: string;
  bonusShare?: PortfolioItem['bonusShare'];
};

const props = withDefaults(defineProps<OrderFormProps>(), {
  dialogId: undefined,
  bonusShare: undefined,
});

const { t } = useI18n();
const { selectedDialogId } = useDialog();
const { openBonusShareDialog } = usePortfolioDialogs();

const PRICE_CONDITIONS = {
  lowerThreshold: { value: 'lowerThreshold', label: t('common.lowerThan') },
  upperThreshold: { value: 'upperThreshold', label: t('common.upperThan') },
};

const BASE_ORDER_FORM: FormModel = {
  quantity: props.model.quantity,
  price: props.model.price,
  totalValue: null,
  priceCondition: PRICE_CONDITIONS.upperThreshold,
  executedQuantity: props.symbol.maximumOrderQuantity,
  validityDate: CONDITIONAL_ORDER_VALIDITY_TIME_PERIOD[1].value,
  bypassSellQuantity: false,
};

const ORDER_CONDITIONS = {
  quantity: 'quantity',
  value: 'value',
};

const { cashData: cash, isLoadingCash } = useLiveCashQuery();

const { findWageConfigBySymbol } = useSymbolDeductions();
const wage = findWageConfigBySymbol(props.symbol);

const {
  openConditionalOrderPriceDescriptionDialog,
  openConditionalOrderDescriptionDialog,
  openAlgorithmOrderRiskDescriptionDialog,
  openPetrochemicalsOrderRiskDescriptionDialog,
} = useOrderDialogs();
const { createConditionalOrder, isLoadingCreateConditionalOrder } =
  useCreateOrder({ isin: props.symbol.isin as string, orderType: props.type });

const shouldCloseDialogAfterOrderExecution = ref(false);
const blockedValue = ref(0);
const orderCondition = ref<string>(ORDER_CONDITIONS.quantity);

const formModel = reactive<FormModel>({ ...BASE_ORDER_FORM });

const isOrderBuy = computed(() => {
  return props.type === 'buy';
});

const realValue = computed(() => {
  if (isOrderBuy.value) {
    return calcSymbolBuyValue(formModel, wage);
  } else {
    return calcSymbolSellValue(formModel, wage);
  }
});
const realValueWords = computed(() => {
  return num2mixed(Math.round(realValue.value / 10));
});
const minOrderValue = computed(() => {
  if (isOrderBuy.value) {
    return props.symbol.minBuyOrderValue <= MINIMUM_CONDITIONAL_TOTAL_VALUE
      ? MINIMUM_CONDITIONAL_TOTAL_VALUE
      : props.symbol.minBuyOrderValue;
  } else {
    return props.symbol.minSellOrderValue <= MINIMUM_CONDITIONAL_TOTAL_VALUE
      ? MINIMUM_CONDITIONAL_TOTAL_VALUE
      : props.symbol.minSellOrderValue;
  }
});

const quantity = computed(() => {
  const roundUp = Boolean(
    formModel.totalValue && formModel.totalValue >= cash.value.buyingPower
  );

  if (isOrderBuy.value) {
    return calcSymbolQuantityBuy(formModel, wage, roundUp);
  } else {
    return calcSymbolQuantitySell(formModel, wage, roundUp);
  }
});

const isValidTotalValue = computed(() => {
  if (formModel.quantity && formModel.price) {
    if (isOrderBuy.value) {
      return formModel.quantity * formModel.price >= minOrderValue.value;
    }

    return formModel.quantity === props.portfolioQuantity
      ? true
      : formModel.quantity * formModel.price >= minOrderValue.value;
  }

  return true;
});

const changeTotalValue = (value: string | number) => {
  if (Number(value) >= 0) {
    formModel.totalValue = Number(value);
  }
};

const changePrice = (value: string | number) => {
  formModel.price = Number(value);
};

const changeQuantity = (value: string | number) => {
  formModel.quantity = Number(value);
};

const onSubmit = (model, { resetForm }) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_ORDER_FORM);
    resetForm({
      values: { ...BASE_ORDER_FORM },
    });
  };

  if (orderCondition.value === ORDER_CONDITIONS.quantity) {
    formModel.totalValue = 0;

    if (!isOrderBuy.value && formModel.quantity === props.portfolioQuantity) {
      formModel.bypassSellQuantity = true;
    }
  } else {
    formModel.quantity = 0;
  }

  formModel.upperThreshold =
    formModel.priceCondition.value === PRICE_CONDITIONS.lowerThreshold.value
      ? formModel.price
      : 0;

  formModel.lowerThreshold =
    formModel.priceCondition.value === PRICE_CONDITIONS.upperThreshold.value
      ? formModel.price
      : 0;

  createConditionalOrder({
    formModel,
    resetFormModel,
    shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
  });
};

eventBus.on('executeOrder', (shouldClose) => {
  if (
    selectedDialogId.value === props.dialogId &&
    !isLoadingCreateConditionalOrder.value
  ) {
    shouldCloseDialogAfterOrderExecution.value = shouldClose;

    onSubmit(formModel, {
      resetForm: () => {
        Object.assign(formModel, BASE_ORDER_FORM);
      },
    });
  }
});

watch(
  () => props.model,
  (newModel) => {
    const sanitizedModel = omitBy(
      newModel,
      (v) => v === null || v === undefined
    );

    if (Object.keys(sanitizedModel).length) {
      Object.assign(formModel, sanitizedModel);
    }

    if (formModel.price) {
      changePrice(formModel.price);

      blockedValue.value = formModel.totalValue ?? 0;
    }

    if (formModel.quantity) {
      changeQuantity(formModel.quantity);

      blockedValue.value = formModel.totalValue ?? 0;
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

const quantityValidationRules = computed(() => {
  if (orderCondition.value === ORDER_CONDITIONS.quantity) {
    return isOrderBuy.value
      ? {
          required: [t('misc.quantity')],
        }
      : {
          required: [t('misc.quantity')],
          upperBound: [
            props.portfolioQuantity,
            t('misc.quantity'),
            t('modules.order.portfolio_quantity'),
          ],
        };
  }

  return {};
});

const totalValueValidationRules = computed(() => {
  if (orderCondition.value === ORDER_CONDITIONS.value) {
    return isOrderBuy.value
      ? {
          required: [t('misc.value')],
          upperBound: [
            cash.value.buyingPower ?? 0,
            t('misc.value'),
            t('common.buy_power'),
          ],
          min: [
            minOrderValue.value,
            t('modules.order.order_value'),
            t('misc.rial'),
          ],
        }
      : {
          required: [t('misc.value')],
          min: [
            minOrderValue.value,
            t('modules.order.order_value'),
            t('misc.rial'),
          ],
        };
  }

  return {};
});
const conditinPriceValueValidationRules = computed(() => {
  if (isValidTotalValue.value) {
    return {
      required: [t('common.price')],
    };
  } else {
    return {
      required: [t('common.price')],

      min: [
        minOrderValue.value,
        t('orders.conditional.minimumTotalValueValidation'),
      ],
    };
  }
});
</script>
